import { Auth } from 'aws-amplify'
import { format } from 'date-fns'
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntercom } from 'react-use-intercom'

import { ProfileInfoFormValues } from '../../../components/profile-info-form'
import { Notify } from '../../../lib/helpers'
import { useUser } from '../../user/user-context'
import { useProfileQueries } from './use-profile-queries'

export const PROFILE_CONTEXT = createContext({})

export interface ProfileContextProviderProps {
    children: React.ReactNode
}

export const ProfileContextProvider = ({
    children,
}: ProfileContextProviderProps) => {
    const { userProfile, user, getUser, getCognitoUser }: any = useUser()
    const { update: updateIntercom } = useIntercom()

    const [formData, setFormData] = useState<ProfileInfoFormValues>({
        country: '',
        dateOfBirth: '',
        email: '',
        firstName: '',
        lastName: '',
        notifyEmail: false,
        notifyPhone: false,
        phone: '',
        productInterests: [],
        availableRoles: [],
        activeRole: 'INDIVIDUAL',
    })

    const updateFormData = useCallback(
        newData => {
            setFormData({ ...formData, ...newData })
        },
        [formData],
    )

    const [loading, setLoading] = useState(false)
    const [verification, setVerification] = useState({
        email: false,
        phone: false,
    })

    const profile = useMemo(
        () => (userProfile ? userProfile.getUserProfile : undefined),
        [userProfile],
    )

    const { update, create }: any = useProfileQueries(profile, user)

    const defaultValues = useMemo<ProfileInfoFormValues>(() => {
        if (!profile) {
            const signUpDefaults: ProfileInfoFormValues = {
                firstName: '',
                lastName: '',
                dateOfBirth: '',
                productInterests: [] as string[],
                country: '',
                availableRoles: ['INDIVIDUAL'],
                activeRole: 'INDIVIDUAL',
            }

            return signUpDefaults
        }

        const defaultFormValues: ProfileInfoFormValues = {
            firstName: profile?.firstName,
            lastName: profile?.lastName,
            dateOfBirth: format(new Date(profile?.dateOfBirth), 'MM/dd/yyyy'),
            productInterests: profile?.interests?.items?.map(
                (x: any) => x?.productInterestId,
            ) as string[],
            country: profile?.countryId,
            email: user?.attributes?.email,
            phone: user?.attributes?.phone_number,
            notifyEmail: profile?.notifyEmail,
            notifyPhone: profile?.notifyPhone,
            availableRoles: profile?.availableRoles,
            activeRole: profile?.activeRole,
        }

        return defaultFormValues
    }, [profile, user])

    const {
        register,
        formState: { errors },
        control,
        formState,
        setError,
    } = useForm<ProfileInfoFormValues>({
        defaultValues,
        shouldUnregister: false,
    })

    useEffect(() => {
        setFormData(defaultValues)
    }, [defaultValues])

    function getAge(DOB: string) {
        var today = new Date()
        var birthDate = new Date(DOB)
        var age = today.getFullYear() - birthDate.getFullYear()
        var m = today.getMonth() - birthDate.getMonth()
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--
        }
        return age
    }

    const validateForm = useCallback(() => {
        if (formData.firstName === '') return false
        if (formData.lastName === '') return false
        if (formData.country === '') return false

        if (formData.phone && formData.phone.length < 5) {
            setError('phone', {
                message: 'Please enter a valid phone number',
            })
            return false
        }

        if (getAge(formData.dateOfBirth) < 17) {
            setError('dateOfBirth', {
                message: 'You must be at least 18 years old',
            })
            return false
        }
        return true
    }, [formData, setError])

    async function handleSubmit(onComplete: () => void) {
        setLoading(true)
        if (!validateForm()) {
            setLoading(false)
            return
        }
        updateIntercom({
            email: formData.email,
            name: formData.firstName + ' ' + formData.lastName,
            phone: formData.phone,
        })
        if (profile) {
            //@ts-ignore
            await update(formData)

            const changedEmail = profile.email !== formData.email
            const changedPhone = profile.phone !== formData.phone

            if (changedEmail || changedPhone) {
                if (changedEmail) {
                    Auth.updateUserAttributes(user, { email: formData.email })
                }
                if (changedPhone) {
                    Auth.updateUserAttributes(user, {
                        phone_number: formData.phone,
                    })
                }
            }
            getUser()
            setTimeout(() => getCognitoUser(), 2000)

            Notify('success', 'Yippee!', 'Your profile has been updated')
        } else {
            await create(formData)
            Notify('success', 'Success!', 'Profile created')
            getUser()
        }
        setLoading(false)

        if (onComplete) onComplete()
    }

    const onSubmit = (onComplete: () => void) => {
        return () => handleSubmit(onComplete)
    }

    const context = {
        profile,
        defaultValues,
        register,
        errors,
        onSubmit,
        control,
        formState,
        loading,
        verification,
        setVerification,
        user,

        formData,
        updateFormData,
    }

    return (
        <PROFILE_CONTEXT.Provider value={context}>
            {children}
        </PROFILE_CONTEXT.Provider>
    )
}
